
// Types
import type {
  Cart,
  CartItem,
  MetaData as HandlePaymentCompleteMetaData,
  Payload as HandlePaymentCompletePayload,
  CheckoutUser as HandlePaymentCompleteUser,
  CancellationProtection,
} from '@white-label-types/parking-checkout';
import type { PusherEvent } from '@white-label-helper/pusher-handle-payment-request';
import type {
  ExtrasCancellationProtectionProduct,
  POI,
  CancellationPolicy,
  AmendmentPolicy,
} from '@white-label-types/parking-booking';

// Packages
import { defineComponent } from 'vue';
import { mapActions, mapState } from 'pinia';
import { Portal, PortalTarget } from 'portal-vue';

// Helpers
import { getAppVariable } from '@white-label-helper/get-app-variable';
import { getAppPaymentProvider } from '@white-label-helper/get-app-payment-provider';
import { trackBeginCheckout } from '@white-label-helper/ga-tracking';
import { fetchCartItems } from '@white-label-helper/api-parking-cart';
import { handlePaymentHelper } from '@white-label-helper/pusher-handle-payment-request';
import { continueBookingHelper } from '@white-label-helper/pusher-continue-booking';
import { updateCancellationProtectionRequestSingleItem, updateCancellationProtectionRequestWholeCart } from '@white-label-helper/api-parkings-cart';
import { parseDiscountCodeData } from '@white-label-helper/discount-code-parser';
import { getFeatureFlag, isInExperiment, trackLaunchDarklyExperimentImpression } from '@white-label-plugin/launch-darkly';
import { addCustomMetric, usePartnerConfigHelper } from '@white-label-helper/shared-helpers';
import { isBookingPortalFlow } from '@white-label-helper/booking-portal-flow';
import { isSycurioFlow } from '@white-label-helper/is-sycurio-flow';
import { trackPaymentPageLoad } from '@white-label-helper/ga-tracking';
import { useScreenSize } from '@white-label-helper/shared-composables';
import { useEventBus } from '@white-label-helper/shared-composables';

// Mixins
import checkout from '@white-label-helper/mixin-checkout';

// Stores
import { usePreloaderControllerStore } from '@white-label-store/preloader-controller';
import { useDiscountCodeStore } from '@white-label-store/discount-code';
import { useCartCheckoutStore } from '@white-label-store/cart-checkout';
import useDepositStore from '@white-label-store/deposits'
import { usePaymentFormStore } from '@white-label-store/payment-form';
import { useTravelInsuranceStore } from '@white-label-store/travel-insurance';
import { useLoungePassStore } from '@white-label-store/lounge-pass';
import { useContentPreloaderStore } from '@white-label-store/content-preloader';

// Constants
import {
  APP_HERO_PRODUCTS,
  ContinueBookingStatuses,
  PAYMENT_PROVIDERS,
  PRODUCT_TYPES,
  ROUTE_NAMES,
  FEATURE_FLAGS,
} from '@white-label-configuration/constants';

import { StatusCodes } from 'http-status-codes';

const {
  handlePayment,
  destroyHandlePayment,
  handleBookingOrderWithoutPayment,
} = handlePaymentHelper('original');
const { continueBooking: continueOriginalBooking, destroyContinueBooking } =
  continueBookingHelper('original');
const eventBus = useEventBus();

import PaymentPageNavigation from '../payment-page-navigation/payment-page-navigation.vue';
import PaymentForm from '../payment-form/payment-form.vue';
import PaymentFormPreloader from '../payment-form-preloader/payment-form-preloader.vue';
import PaymentSummary from '../payment-summary/payment-summary.vue';
import PaymentSummaryDropdown from '../payment-summary-dropdown/payment-summary-dropdown.vue';
import SummaryPreloader from '../summary-preloader/summary-preloader.vue';
import StickySidebar from '../sticky-sidebar/sticky-sidebar.vue';
import PaymentPageCountdown from '../payment-page-countdown/payment-page-countdown.vue';
import { getAppHeroProduct } from '@white-label-helper/get-app-hero-product';
import { userDetails } from '@white-label-helper/api-parkings-user';

export default defineComponent({
  name: 'PaymentPage',

  handlePayment,
  destroyHandlePayment,
  continueOriginalBooking,
  destroyContinueBooking,
  handleBookingOrderWithoutPayment,

  components: {
    PaymentPageNavigation,
    PaymentForm,
    PaymentFormPreloader,
    Portal,
    PortalTarget,
    StickySidebar,
    PaymentSummary,
    PaymentSummaryDropdown,
    SummaryPreloader,
    PaymentPageCountdown
  },
  setup() {
    const { isMobileResolution, isDesktopResolution } = useScreenSize();
    return { isMobileResolution, isDesktopResolution };
  },
  mixins: [checkout],

  data() {
    return {
      isBookingPortalSettlementModalVisible: false,
      loadingPaymentInfo: false,
      $vueInstance: null as Vue | null,
    }
  },

  computed: {
    ...mapState(useCartCheckoutStore, {
      readCartItems: 'readCartItems',
      readCartBundle: 'readCartBundle'
    }),
    isOnTravelExtrasPage(): boolean {
      return this.$route.name === ROUTE_NAMES.travel_extras
    },
    ...mapState(useTravelInsuranceStore, {
      travelInsuranceCartItem: 'travelInsuranceCartItem',
    }),
    ...mapState(useLoungePassStore, {
      loungePassItem: 'loungePassItem',
      hasLoungePassItem:
        'hasLoungePassItem',
    }),
    ...mapState(useDiscountCodeStore, {
      discountCode: 'discountCode',
      discountObject: 'discountObject',
      discountQuery: 'discountQuery',
    }),
    ...mapState(usePaymentFormStore, {
      readPaymentFormData: 'readPaymentFormData',
      isApplePay: 'isApplePay',
    }),
    ...mapState(useContentPreloaderStore, {
      contentPreloaderEnabled: 'enabled',
    }),
    isMultiBasket(): boolean {
      return !!getAppVariable('is_multi_basket');
    },
    displayNewSummaries() {
      return this['$launchDarkly'].variation(FEATURE_FLAGS.PT_1566_DISPLAY_NEW_PAYMENT_SUMMARIES);
    },
    isPaymentPageContentLoading(): boolean {
      return this.contentPreloaderEnabled || this.isBookingPortalSettlementModalVisible;
    },
    cartItems() {
      return this.readCartItems;
    },
    paymentFormData(){
      return this.readPaymentFormData;
    },
    additionalFields() {
      return this.checkout_cartItems.length > 1
        ? this.checkout_multiCartAdditionalFields
        : this.checkout_additionalFields;
    },
    cancellationProtection(): CancellationProtection[] {
      // Always treat cancellationProtection as an array
      return this.checkout_multiCartCancellationProtection
    },
    cancellationProtectionNonBundleProducts(): string[] {
      return this.cancellationProtection
          .filter((item) => !item?.is_bundle_product && item?.is_available)
        .map((item) => item?.product_id) || [];
    },
    isProtected(): boolean {
      return this.cartItems?.some((item: CartItem) => item.is_protected);
    },
    cancellationPolicies(): CancellationPolicy[] | CartItem['inventory_option']['cancellation_policies'][] {
      return this.checkout_multiCartCancellationPolicies
    },
    amendmentPolicies(): AmendmentPolicy[] | CartItem['inventory_option']['amendment_policies'][] {
      return this.checkout_multiCartAmendmentPolicies
    },
    isBookingFeeCharged(): boolean {
      return this.checkout_bookingFee?.is_enabled || false;
    },
    bundle() {
      return this.readCartBundle;
    },
    isBundleCancellationProtectionAutoAdded(): boolean {
      return this.readCartBundle?.is_cancellation_protection_auto_add || false;
    },

    /**
     * The travel extras page is active if:
     * - the cross sell flow is enabled by the partner setup
     * - the `skippedExtrasStep` flag from local storage does not match the current cart token
     */
    travelExtrasPageActive() {
      const { isCrossSellFlowEnabled } = usePartnerConfigHelper();
      const skippedExtrasStep = (localStorage.getItem('skippedExtrasStep') ?? '') === this.checkout_cartToken;
      return isCrossSellFlowEnabled && !skippedExtrasStep;
    },
    isPaymentPageCountdown(): boolean {
      return getFeatureFlag(FEATURE_FLAGS.CRO_1064_SHOW_COUNTDOWN_ON_CHECKOUT, false);
    },
    isWindcaveNewFlow(): boolean {
      return this.checkout_isWindcavePayment
        && getFeatureFlag(FEATURE_FLAGS.ON_2258_MODIFY_WINDCAVE_FLOW, false)
        && !this.isApplePay;
    },
  },
  created() {
    this.commitCartItems([]);
  },

  mounted() {
    this.$vueInstance = this.$root;
    const { skipExtras: skippedExtrasFromRoute, ...remainingQuery } = this.$route.query;
    const skippedExtrasFromLocalStorage = localStorage.getItem('skippedExtrasStep') === this.checkout_cartToken;
    const skippedExtras = (
      skippedExtrasFromRoute === 'true'
      || skippedExtrasFromLocalStorage
    );
    if (skippedExtras) {
      localStorage.setItem('skippedExtrasStep', this.checkout_cartToken);
      this.$router.replace({ query: remainingQuery });
    } else {
      localStorage.setItem('skippedExtrasStep', '');
    }

    if (isInExperiment(FEATURE_FLAGS.CRO_534_SECURE_PAYMENT_MESSAGE_ON_TOP) && getFeatureFlag(FEATURE_FLAGS.CRO_534_SECURE_PAYMENT_MESSAGE_ON_TOP) === true && this.$route.query['test'] !== 'cro-534') {
      this.$router.replace({
        query: {
          ...this.$route.query,
          test: 'cro-534',
        }
      });
    };

    if (isInExperiment(FEATURE_FLAGS.CRO_1027_DISCOUNT_CODE_PAYMENT_SECTION) && getFeatureFlag(FEATURE_FLAGS.CRO_1027_DISCOUNT_CODE_PAYMENT_SECTION) === true && this.$route.query['test'] !== 'cro-1027') {
      this.$router.replace({
        query: {
          ...this.$route.query,
          test: 'cro-1027',
        }
      });
    };

    trackLaunchDarklyExperimentImpression(this.$gtm, FEATURE_FLAGS.CRO_1064_SHOW_COUNTDOWN_ON_CHECKOUT, 'cro-1311');

  //for payment summary - so it doesn't overlap with the footer on mobile
    this.$nextTick(() => {
      this.initSummaryHeightObserver();
      window.addEventListener('resize', this.updateSummaryHeight);
    });
  },

  beforeDestroy() {
    this.setBookingPortalSettlementModalVisibility(false);
    if (this._resizeObserver) {
      this._resizeObserver.disconnect();
    }
    window.removeEventListener('resize', this.updateSummaryHeight);
    document.documentElement.style.setProperty('--summary_bottom_height', '0px');
  },

  methods: {
    ...mapActions(usePreloaderControllerStore, {
      commitUpdatePreloader: 'updatePreloader',
      commitUpdateBookingPortalSettlementModalVisibility: 'updateBookingPortalSettlementModalVisibility',
    }),

    ...mapActions(useCartCheckoutStore, {
      commitCartItems: 'commitCartItems',
      commitCartBundle: 'commitCartBundle',
      commitUpdateCancellationProtection: 'commitUpdateCancellationProtection',
    }),

    ...mapActions(useDiscountCodeStore, {
      storeDiscountCode: 'storeDiscountCode',
      storeDeepLinkCode: 'storeDeepLinkCode',
      clearDiscountCode: 'clearDiscountCode'
    }),

    ...mapActions(useContentPreloaderStore, {
      updatePreloaderStatus: 'updateStatus',
    }),

    cartHasExtrasProduct() {
      return this.cartItems?.some(
        (item: CartItem) => item.product_code === PRODUCT_TYPES.EXTRAS
      );
    },
    async getCart() {
      try {
        let cartItems: Cart;
        cartItems = (await fetchCartItems(
          this.checkout_cartToken
        )) as Cart;
        const deposit = useDepositStore()
        deposit.updateCartDepositTotal({
          ...cartItems.payable,
        });
        deposit.updateItemDeposit(cartItems.items)
        this.commitCartItems(cartItems.items);
        if (cartItems.bundle) {
          this.commitCartBundle({
            ...cartItems.bundle,
              items: [...cartItems.items.filter((item) => item.bundle_id === cartItems.bundle.id)],
          });
        }
        // now we get a successful response even for a non-existent cart
        // to check if cart not empty we look at the length of the array
        if (cartItems.items.length) {
          // if we have discount code applied to the cart – put it to store to be displayed
          const discountAppliedToCart = parseDiscountCodeData(cartItems);
          if (discountAppliedToCart) {
            this.storeDiscountCode(discountAppliedToCart);
          }

          const {
            items,
            valid_till,
            is_valid,
            totals,
            original_amounts,
            booking_fee,
          } = cartItems;

          if (deposit.shouldShowDepositInformation) {
            totals['total'] = cartItems.payable.pay_now;
          }

          const item = items[0];
          const couponCode = item.search_criteria?.parking?.coupon_code;
          const groups = item.search_criteria?.parking?.groups;
          this.handleCouponCode(items, couponCode);

          this.checkout_cartItems = items;
          this.checkout_originalAmounts = original_amounts;
          this.checkout_bookingFee = booking_fee;
          this.checkout_total = totals;
          // @ts-ignore – Mixin method
          this.checkout_revalidateCartRecursive(is_valid, true, valid_till);

          this.updatePreloaderStatus(false);

          const query = this.checkout_cartItem.search_criteria[this.productCode] || {};

          // If multiple POIs are available, we need to get the correct one
          const poi = getAppVariable('pois').find(
            (poi: POI) => poi.code === query.airport_code
          );

          trackPaymentPageLoad({
            gtm: this.$gtm,
            cartItems: items,
            primaryProductType: getAppHeroProduct() as string,
            poi: {
              code: poi?.code,
              name: poi?.name,
            },
            authData: {
              isAuthenticated: this.$auth.isAuthenticated || false,
              userId: this.$auth.user?.sub,
            },
          });

          const trackingItems = [...items.map((item) => ({
              ...item,
              bundle_name: cartItems.bundle?.name || '',
          }))];

          const metaIndex = item?.meta?.filter?.index;
          const metaItemListName = item?.meta?.filter?.item_list_name;
          const bookingFee = booking_fee?.amount;

          const params =
            metaIndex && metaItemListName
              ? [this.$gtm, trackingItems, bookingFee, metaIndex, metaItemListName]
              : [this.$gtm, trackingItems, bookingFee];

          if (this.isLoungesProduct) {
            params.push('lounges');
          }

          // @ts-ignore - Wrong product type
          trackBeginCheckout(...params);

          // Track generic LaunchDarkly 'begin_checkout' event
          this.$launchDarkly.track('begin_checkout');
        } else {
          // @ts-ignore - Mixin method
          this.checkout_showTechnicalIssueError();
        }
      } catch (error: unknown) {
        let errorResponse = '';
        try {
          errorResponse = JSON.parse(error.message);
        } catch (parseError) {}

        // Session expired
        if (errorResponse?.status === StatusCodes.GONE) {
          // @ts-ignore - Mixin method
          this.checkout_openExpiredModal();
          return;
        }

        // @ts-ignore - Mixin method
        this.checkout_showTechnicalIssueError();
      }
    },

    handleCouponCode(items, couponCode) {
      const discountApplied = items.some(item => item?.discount?.code);
      if (typeof couponCode === 'string' && couponCode && discountApplied) {
        this.storeDeepLinkCode(couponCode);
      } else {
        this.storeDeepLinkCode('');
      }
    },

    setBookingPortalSettlementModalVisibility(isVisible: boolean) {
      this.isBookingPortalSettlementModalVisible = isVisible;
      this.commitUpdateBookingPortalSettlementModalVisibility(isVisible);
    },

    showBookingPortalSuccessModal(email = '') {
      this.setBookingPortalSettlementModalVisibility(true);

      return this.$openModal('GlobalModalError', {
        header: this.$t('UI.modalSuccess.bookingSuccessfullyUpdated.header'),
        body: this.$t('UI.modalSuccess.bookingSuccessfullyUpdated.body', { email }),
        isSuccess: true,
        btnText: this.$t('UI.button.continue'),
      })
        .then(() => this.$auth.logout())
        .catch(() => {
          this.setBookingPortalSettlementModalVisibility(false);
        });
    },

    showBookingPortalTechnicalErrorModal() {
      this.setBookingPortalSettlementModalVisibility(true);

      return this.$openModal('GlobalModalError', {
        header: this.$t('UI.modalError.header'),
        body: this.$t('UI.modalError.bookingTechError'),
        btnText: this.$t('UI.button.continue'),
      })
        .then(() => {
          this.commitUpdatePreloader('');
          this.setBookingPortalSettlementModalVisibility(false);
        })
        .catch(() => {
          this.setBookingPortalSettlementModalVisibility(false);
        });
    },

    handlePaymentCompleted(payload: {
      payload: HandlePaymentCompletePayload;
      userDetails: HandlePaymentCompleteUser;
      metaData: HandlePaymentCompleteMetaData;
    }) {
      if (
        (getAppPaymentProvider() === PAYMENT_PROVIDERS.STRIPE || this.checkout_isAdvamPayment || this.isWindcaveNewFlow) &&
        this.checkout_orderTotal !== 0
      ) {
        this.continueBooking(ContinueBookingStatuses.Successful);
      } else {
        this.createBooking(payload);
      }
    },

    handleBookingCreation(payload: {
      payload: HandlePaymentCompletePayload;
      userDetails: HandlePaymentCompleteUser;
      metaData: HandlePaymentCompleteMetaData;
    }) {
      this.createBooking(payload);
    },

    processTravellersForTravelInsuranceBooking(leadTravellerDetails: HandlePaymentCompleteUser) {
      const travellers = [];
      const travelInsuranceStore = useTravelInsuranceStore();
      const travelInsuranceCartItem = travelInsuranceStore.travelInsuranceCartItem;
      const leadTravellerForTravelInsurancePayload = travelInsuranceCartItem.search_criteria.travel_insurance.lead_traveller;
      travellers.push({
        first_name: leadTravellerDetails.first_name,
        last_name: leadTravellerDetails.last_name,
        cart_item_id: travelInsuranceCartItem.id,
        date_of_birth: leadTravellerForTravelInsurancePayload.date_of_birth,
        is_lead: true,
        country_of_residence: leadTravellerForTravelInsurancePayload.country_of_residence,
      });

      const additionalTravellers = this.$refs.paymentForm.additionalTravellersDetails;
      if (additionalTravellers?.length) {
        for (const traveller of additionalTravellers) {
          travellers.push({
            first_name: traveller.firstName,
            last_name: traveller.lastName,
            cart_item_id: travelInsuranceCartItem.id,
            date_of_birth: traveller.dateOfBirth || traveller.date_of_birth,
            is_lead: false,
            country_of_residence: traveller.country_of_residence,
          });
        }
      }

      return travellers;
    },

    processTravellersForLoungePassBooking(leadTravellerDetails: HandlePaymentCompleteUser) {
      const travellers = [];
      if (this.hasLoungePassItem) {
        travellers.push({
          first_name: leadTravellerDetails.first_name,
          last_name: leadTravellerDetails.last_name,
          cart_item_id: this.loungePassItem.id,
          age_group: 'adult',
          is_lead: true,
        });

        const additionalTravellers =
          this.$refs.paymentForm.loungePassAdditionalTravellersDetails;
        if (additionalTravellers?.length) {
          for (const traveller of additionalTravellers) {
            // If additional travellers details are not required, only include them in the payload if they have a first, last name and age group.
            // If details are required, include all additional travellers,
            // as the booking API will return an error if any additional traveller is missing required details.
            if (!this.$refs.paymentForm.isLoungePassAdditionalTravellersDetailsRequired) {
              if (traveller.firstName && traveller.lastName && traveller.ageGroup) {
                travellers.push({
                  first_name: traveller.firstName,
                  last_name: traveller.lastName,
                  cart_item_id: this.loungePassItem.id,
                  age_group: traveller.ageGroup,
                  is_lead: false,
                });
              }
            } else {
              travellers.push({
                first_name: traveller.firstName,
                last_name: traveller.lastName,
                cart_item_id: this.loungePassItem.id,
                age_group: traveller.ageGroup,
                is_lead: false,
              });
            }
          }
        }
      }

      return travellers;
    },

    async createBooking({
      payload,
      userDetails,
      metaData,
    }: {
      payload: HandlePaymentCompletePayload;
      userDetails: HandlePaymentCompleteUser;
      metaData: HandlePaymentCompleteMetaData;
    }) {
      const startTime = performance.now();
      const getOutboundFlight = (
        userDetails: HandlePaymentCompleteUser,
        cartItem: CartItem,
      ) => {
        // note that this has a single use-case - for TimeSlot to automatically
        // include the flight number on the booking.
        type TimeslotCriteria = { airline_iata?: string, flight_number?: string } | undefined;
        const { flight_number, airline_iata } = cartItem.search_criteria?.[APP_HERO_PRODUCTS.FASTTRACK] as TimeslotCriteria ?? {};
        if (getFeatureFlag(FEATURE_FLAGS.NGQ_11_TIMESLOT) && airline_iata && flight_number) return `${airline_iata}${flight_number}`;
        return userDetails.outbound_flight;
      };
      const body = {
        cart_token: this.checkout_cartToken,
        amount: this.checkout_orderTotal,
        currency: this.checkout_currency,
        user: {
          ...userDetails,
          outbound_flight: getOutboundFlight(userDetails, this.checkout_cartItem),
        },
        travellers: [],
        ...(!isBookingPortalFlow()
          ? {
              gateway_data: this.checkout_getGatewayData(payload),
            }
          : {}),
        ...(isBookingPortalFlow()
          ? {
              meta: {
                ...metaData,
              },
            }
          : {}),
      };
      if (this.travelInsuranceCartItem) {
        body.travellers = body.travellers.concat(this.processTravellersForTravelInsuranceBooking(userDetails));
      }

      if (this.hasLoungePassItem) {
        body.travellers = body.travellers.concat(this.processTravellersForLoungePassBooking(userDetails));
      }

      this.commitUpdatePreloader(this.$t('UI.fullscreenPreloaderMessages.nearlyThere'));
      if (isBookingPortalFlow()) {
        this.$options.handleBookingOrderWithoutPayment(this.checkout_cartToken, body)
          .then(() => {
            this.commitUpdatePreloader('');
            return this.showBookingPortalSuccessModal(body.user.email || '');
          }).then(() => {
            addCustomMetric("tracking", "Booking Portal Metric: Success", {
              event: 'BookingSuccess'
            },  startTime, 'booking_service');
          })
          // @ts-ignore mixin method
          .catch(() => {
            this.showBookingPortalTechnicalErrorModal();
      })
      } else {
        handlePayment(this.checkout_cartToken, body, this.checkout_pusherChannel)
          .then((data: PusherEvent) => {
            this.checkout_forwardInfo = data.forward;

            if ((getAppPaymentProvider() === PAYMENT_PROVIDERS.STRIPE && this.checkout_orderTotal !== 0)) {
              this.checkout_paymentsConfig.token = data.payment_client_secret;
              this.checkout_paymentIntentUpdated = true;
            } else if ((this.checkout_isAdvamPayment || this.isWindcaveNewFlow) && this.checkout_orderTotal !== 0) {
              eventBus.$emit('booking-created', true);
            } else {
              this.continueBooking(ContinueBookingStatuses.Successful);
            }
          })
          .catch((error) => {
            if (this.checkout_isWindcavePayment && !this.isWindcaveNewFlow) {
              this.checkout_handleVoid(payload);
            }

            if (this.checkout_isAdvamPayment) {
              eventBus.$emit('booking-created', false);
            }

            this.checkout_handlePaymentError(error);
          });
      }
    },

    continueBooking(status: string) {
      const body = {
        status,
        // @ts-ignore mixin variable
        forward: this.checkout_forwardInfo,
      };
      continueOriginalBooking(body, this.checkout_pusherChannel)
        .then(
          ({
            management_token,
            order_reference,
          }: {
            management_token: { token: string; expiration: number };
            order_reference: string;
          }) => {
            // @ts-ignore - Mixin method
            this.checkout_trackOnBookingComplete(
              {
                id: order_reference,
                affiliation: getAppVariable('partner_name'),
                revenue: this.checkout_orderTotal,
                coupon: '',
              },
              {
                name: this.checkout_orderName,
                id: this.checkout_cartItem.id,
                price: this.checkout_orderTotal,
                category: this.checkout_cartItem.product_code,
                quantity: 1, // Quantity, set to 1 for MVP
              }
            );

            const routerKeyMap = {
              booking: 'BOOKING_RECEIPT',
            }

            const getRouterKey = routerKeyMap.booking;
            // @ts-ignore - Mixin method
            this.checkout_onSuccessCleaningStorage();

            // @ts-ignore - Mixin method
            this.checkout_setCookiesAndGoToReceipt(
              management_token,
              getRouterKey,
            );
          }
        ).catch((error: { errorType: string; error: string; type: string }) => {
          if (status === ContinueBookingStatuses.Failed) {
            this.checkout_forwardInfo = null;
            this.commitUpdatePreloader('');
          } else {
            // @ts-ignore mixin method
            this.checkout_handlePaymentError(error);
          }
        });
    },

    updateItems(items: CartItem[] | []) {
      this.checkout_cartItems = items;
    },

    updateOrderTotals(orderTotals: Cart['totals']) {
      this.checkout_total = orderTotals;
    },

    handleAddDiscountError() {
      // If we receive an error when adding a discount and another discount was added before that
      // We return the values to the original (without discount) from 'original_amounts' property

      if (this.checkout_cartItem.discount && this.discountCode) {
        this.clearDiscountCode();
        if (this.isParkingProduct) {
          this.checkout_total = this.checkout_originalAmounts;
          this.checkout_cartItems = [
            {
              ...this.checkout_cartItem,
              totals: this.checkout_cartItem.original_amounts.totals,
              fees: this.checkout_cartItem.original_amounts.fees,
              taxes: this.checkout_cartItem.original_amounts.taxes,
              discount: null,
            },
          ];
        } else if (this.isLoungesProduct) {
          this.checkout_total = this.checkout_originalAmounts;
          this.checkout_cartItems = this.cartItems
        }
      }
    },

    showPaymentFailedModal() {
      setTimeout(() => {
        this.$vueInstance.$openModal('GlobalModalError', {
          header: this.$vueInstance.$t('UI.modalError.paymentError.header'),
          body: this.$vueInstance.$t('UI.modalError.paymentError.body'),
          buttons: [
            {
              btnText: this.$vueInstance.$t('shared.buttons.close'),
            },
          ],
        });
      }, 500);
    },

    /**
     * Marks the booking as failed and triggers the continue-booking flow.
     */
    failBooking() {
      this.continueBooking(ContinueBookingStatuses.Failed);
    },

    handleWidgetError() {
      // @ts-ignore mixin variable
      if (this.checkout_forwardInfo) {
        this.failBooking();
        this.checkout_paymentIntentUpdated = false;
        this.showPaymentFailedModal();
      }
    },

    async addCancellationProtection(): Promise<void> {
      this.loadingPaymentInfo = true;

      if (this.isBundleCancellationProtectionAutoAdded) {
        await this.toggleCancellationProtectionSpecificItems(this.cancellationProtectionNonBundleProducts, true);
      } else {
        await this.toggleCancellationProtectionWholeCart(true);
      }

      this.loadingPaymentInfo = false;
      if (this.checkout_isWindcavePayment || this.checkout_isAdvamPayment || isSycurioFlow(this.$route)) {
        await this.checkout_handlePaymentReload();
      }
    },

    async removeCancellationProtection(): Promise<void> {
      this.loadingPaymentInfo = true;

      if (this.isBundleCancellationProtectionAutoAdded) {
        await this.toggleCancellationProtectionSpecificItems(this.cancellationProtectionNonBundleProducts, false);
      } else {
        await this.toggleCancellationProtectionWholeCart(false);
      }

      this.loadingPaymentInfo = false;
      if (this.checkout_isWindcavePayment || this.checkout_isAdvamPayment || isSycurioFlow(this.$route)) {
        await this.checkout_handlePaymentReload();
      }
    },

    async toggleCancellationProtectionSpecificItems(productIds: string[], isProtected: boolean): Promise<void> {
      const toggleCancellationProtectionRecursive = async (index: number) => {
        if (index >= productIds.length) {
          return;
        }

        const productId = productIds[index];
        const { totals, payable } = await updateCancellationProtectionRequestSingleItem<
            ExtrasCancellationProtectionProduct,
            Cart['totals'],
            Cart['payable']
          >(productId, this.checkout_cartToken, {
            is_protected: isProtected,
          });

        this.checkout_total = totals;
        const deposit = useDepositStore();
        deposit.updateCartDepositTotal({...payable });
        this.commitUpdateCancellationProtection({ productId, isProtected });

        await toggleCancellationProtectionRecursive(index + 1);
      };

      await toggleCancellationProtectionRecursive(0);
    },

    async toggleCancellationProtectionWholeCart(isProtected: boolean): Promise<void> {
      const { items, totals, payable } = await updateCancellationProtectionRequestWholeCart<
          ExtrasCancellationProtectionProduct,
          Cart['totals'],
          Cart['payable']
      >(this.checkout_cartToken, {
        is_protected: isProtected,
      });

      this.checkout_total = totals;
      const deposit = useDepositStore();
      deposit.updateCartDepositTotal({...payable });
      Array.isArray(items) && items.forEach((item) => {
        this.commitUpdateCancellationProtection({ productId: item.id, isProtected: item.is_protected });
      });
      this.updateItems(items);
    },

    appendOrderReferenceToReturnUrl(returnUrl?: string, orderReference?: string): string | undefined {
      if (!returnUrl || !orderReference) {
        return returnUrl;
      }

      try {
        const url = new URL(returnUrl, window.location.origin);
        url.searchParams.set('order-reference', orderReference);
        return url.toString();
      } catch {
        const separator = returnUrl.includes('?') ? '&' : '?';
        return `${returnUrl}${separator}order-reference=${encodeURIComponent(orderReference)}`;
      }
    },

    //for payment summary - so it doesn't overlap with the footer on mobile
    updateSummaryHeight() {
      const stickyEl = this.$el.querySelector('.sticky-order-summary');
      const isMobile = window.innerWidth < 768;
      if (isMobile && stickyEl) {
        const height = stickyEl.offsetHeight;
        document.documentElement.style.setProperty(
          '--summary_bottom_height', `${height}px`
        );
      } else {
        document.documentElement.style.setProperty('--summary_bottom_height', '0px');
      }
    },

    initSummaryHeightObserver() {
      const stickyEl = this.$el.querySelector('.sticky-order-summary');
      if (stickyEl) {
        this._resizeObserver = new ResizeObserver(() => {
          requestAnimationFrame(() => {
            this.updateSummaryHeight();
          });
        });
        this._resizeObserver.observe(stickyEl);
      }
    },
  },
});
